import * as path from "path";
import * as fs from "mz/fs";
import { map } from "@pagedip/util-promise";
import * as npmUtil from "@pagedip/util-npm";
import log, { PrefixedLogger } from "@pagedip/util-logger";
import { QueryGraph, PackageGraph } from "@pagedip/util-lerna";
import { getCommits, Commit, resolveGitRef, dargs } from "./git";
import { Release, VersionChange, resolveVersionChange } from "./release";
import { Env } from "./env";
import * as formats from "./formats";
import { isNonEmptyString, createTemplate } from "./util";
import { difference } from "lodash";
import npa from "npm-package-arg";

export namespace Package {
  export interface ResolveCommitOptions {
    basedir?: string;
    commitCache?: Map<string, Commit>;
    refCache?: Map<string, string | undefined>;
  }

  export interface LoadOptions extends ResolveCommitOptions {
    registryUrl?: string;
  }

  export interface LoadLernaGraphOptions extends LoadOptions {
    getRegistryUrl?: (pkg: npmUtil.Package) => string;
    progress?: (pkg: Lerna.Package, total: number) => any;
    defaultRelease?: Release;
  }

  export type Dependencies = Partial<Record<string, string>>;

  export type DepType = "dependencies" | "devDependencies" | "peerDependencies" | "optionalDependencies";
}

export class Package {
  static formats: { [key: string]: string } = formats;

  static depKeys = new Set<Package.DepType>([
    "dependencies",
    "devDependencies",
    "peerDependencies",
    "optionalDependencies",
  ]);

  static hasDepsChanged(deps?: Package.Dependencies, prev?: Package.Dependencies, ignore?: Set<string>) {
    if (prev == null || deps == null) return !(prev == null && deps == null);

    ignore = ignore || new Set();
    const prevDeps = Object.keys(prev);
    const nextDeps = Object.keys(deps);

    // check for the same deps, changing order doesn't matter
    // ignored items are included because that's still considered a change
    if (nextDeps.length !== prevDeps.length || difference(nextDeps, prevDeps).length) {
      return true;
    }

    // check the actual versions for changes
    for (const dep of nextDeps) {
      if (!ignore.has(dep) && deps[dep] !== prev[dep]) {
        return true;
      }
    }

    // otherwise nothing has changed
    return false;
  }

  static updateDeps(deps?: Package.Dependencies, packages?: Map<string, Package>) {
    const result: Package.Dependencies = {};
    if (deps == null) return result;
    if (packages == null) return deps;

    for (const depname of Object.keys(deps)) {
      const dep = packages.get(depname);
      let version = "";

      if (dep) {
        version = dep.version.next || dep.version.anchor || "";
        if (!version && dep.anchor) version = dep.anchor.version;
        if (version) version = "^" + version;
      }

      result[depname] = version || deps[depname];
    }

    return result;
  }

  static async loadLernaGraph(packages: Lerna.Package[], options?: Package.LoadLernaGraphOptions) {
    options = options || {};
    const { getRegistryUrl, registryUrl, commitCache, refCache, progress, basedir, defaultRelease } = options;
    const graph = new PackageGraph(packages);
    const sortedPackages = QueryGraph.toposort(packages);
    const result = new Map<string, Package>();

    const commitOpts: Package.ResolveCommitOptions = {
      basedir,
      commitCache: commitCache || new Map(),
      refCache: refCache || new Map(),
    };

    const orderedResult = await map(
      sortedPackages,
      async (lernapkg) => {
        // call progress
        if (progress) progress(lernapkg, sortedPackages.length);

        // coerced into an NPM package because types don't match but it is the correct object
        const pkgjson = lernapkg.toJSON() as npmUtil.Package;

        // load release file
        let release = await Release.load(lernapkg.location);
        if (defaultRelease) release = Release.merge(defaultRelease, release);

        // fetch from registry
        const registry = getRegistryUrl ? getRegistryUrl(lernapkg) : registryUrl;
        const info = await npmUtil.fetchPackageInfo(registry, pkgjson.name);

        // load the base package
        const pkg = new Package(lernapkg.location, pkgjson, release, info);

        // resolve all commits since the last release
        await pkg.resolveCommits(commitOpts);

        // resolve the local dependencies from the lerna package graph
        pkg.resolveLocalDependencies(graph);

        // add package to map
        result.set(pkg.name, pkg);

        return pkg;
      },
      {
        concurrency: 8,
      }
    );

    // patch any package that has a local dependency releasing
    for (const pkg of orderedResult) {
      if (pkg.release.type !== "skip") continue;

      if (pkg.hasDependencyBreakingRelease(result)) {
        pkg.release.type = "major";
        pkg.release.reason = "local dependency breaking change";
        pkg.resolveVersion();
      } else if (
        pkg.hasDepsChanged("dependencies") ||
        pkg.hasDepsChanged("peerDependencies") ||
        pkg.hasDepsChanged("optionalDependencies")
      ) {
        pkg.release.type = "patch";
        pkg.release.reason = "dependencies changed";
        pkg.resolveVersion();
      }
    }

    // wrap calculations with all packages
    for (const pkg of orderedResult) {
      pkg.resolveDependencies(result);
      pkg.validate(result);
    }

    return result;
  }

  static async load(location?: string, options?: Package.LoadOptions) {
    options = options || {};
    const { basedir, registryUrl, ...opts } = options;

    location = path.resolve(basedir || ".", location || ".");
    const src = await fs.readFile(path.join(location, "package.json"), "utf8");
    const pkgjson: npmUtil.Package = JSON.parse(src);

    if (!pkgjson.name) {
      throw new Error("package.json missing name field");
    }

    const release = await Release.load(location);
    const info = await npmUtil.fetchPackageInfo(registryUrl, pkgjson.name);

    // build package object
    const pkg = new Package(location, pkgjson, release, info);

    // resolve all commits since the last release
    await pkg.resolveCommits({
      ...opts,
      basedir,
    });

    // validate the package
    pkg.validate();

    return pkg;
  }

  logger: PrefixedLogger;
  /** path on the filesystem to this package */
  location: string;
  /** a previous release that is used as the basis for the next release */
  anchor?: npmUtil.Package;
  /** names of all dependencies within the same project as this package, excluding those in devDependencies */
  localDependencies = new Set<string>();
  /** published dependencies (local deps have version filled, use this.pkg for true deps) */
  dependencies: Package.Dependencies = {};
  /** published dev dependencies (local deps have version filled, use this.pkg for true deps) */
  devDependencies: Package.Dependencies = {};
  /** published peer dependencies (local deps have version filled, use this.pkg for true deps) */
  peerDependencies: Package.Dependencies = {};
  /** published optional dependencies (local deps have version filled, use this.pkg for true deps) */
  optionalDependencies: Package.Dependencies = {};
  /** the base commit hash for determining if the package has been modified */
  anchorCommit?: string;
  /** list of commits that apply to this package */
  commits: Commit[] = [];

  validations = {
    warnings: new Set<string>(),
    errors: new Set<string>(),
  };

  /** how the version will change with the next release */
  version: VersionChange = {
    type: "skip",
    anchor: "1.0.0",
  };

  constructor(
    /** path to package directory */
    location: string | undefined,
    /** package.json data from the file system */
    public pkg: npmUtil.Package,
    /** config that dictates how the release behaves for this package */
    public release: Release,
    /** full registry information for package */
    public info?: npmUtil.PackageInfo
  ) {
    this.logger = log.prefix(`autorelease[${pkg.name}]`);
    this.location = path.resolve(location || ".");
    this.anchor = info ? npmUtil.getRelease(info, this.anchorTags) : undefined;
    this.resolveVersion();
    this.resolveDependencies();
  }

  static isPackage(p: any): p is Package {
    return Boolean(p && p.__autorelease_package__ === true);
  }

  get __autorelease_package__() {
    return true;
  }

  get name() {
    return this.pkg.name;
  }

  get canRelease() {
    return this.release.type !== "skip" && !this.pkg.private;
  }

  get anchorTags() {
    return [this.release.version, this.release.distTag, "latest"].filter(isNonEmptyString);
  }

  get distTag() {
    return this.release.distTag || "latest";
  }

  get access() {
    // non-scoped packages can't be anything but public
    if (this.name[0] !== "@") return "public";
    if (this.release.access) return this.release.access;
    if (typeof this.pkg.public !== "undefined") return this.pkg.public ? "public" : "restricted";
    return "restricted";
  }

  get gitRefs() {
    return [this.release.gitRef, this.anchor ? this.anchor.gitHead : undefined].filter(isNonEmptyString);
  }

  /**
   * Check the package.json for a specific dependency. Optionally scope by dep type.
   * @param dep Name of a dependency to check for.
   * @param types List of dep types (dependencies, devDependencies, etc) to check under.
   */
  hasDependency(dep: string, types: Iterable<Package.DepType> = Package.depKeys) {
    for (const t of types) {
      if (this.pkg[t] != null && Object.prototype.hasOwnProperty.call(this.pkg[t], dep)) {
        return true;
      }
    }

    return false;
  }

  resolveLocalDependencies(graph: Lerna.PackageGraph) {
    this.localDependencies.clear();

    const node = graph.get(this.name);

    if (node) {
      for (const dep of node.localDependencies.values()) {
        if (dep.name && this.hasDependency(dep.name, ["dependencies", "optionalDependencies"])) {
          this.localDependencies.add(dep.name);
        }
      }

      // regular lerna graph does not handle peer dependencies so we have to do that manually
      if (this.pkg.peerDependencies) {
        for (const [depName, value] of Object.entries(this.pkg.peerDependencies)) {
          if (!value) continue;

          const depNode = graph.get(depName);
          const spec = value.replace(/^link:/, "file:");
          const resolved = npa.resolve(depName, spec, node.location);

          if (depNode && (resolved.fetchSpec === depNode.location || depNode.satisfies(resolved))) {
            this.localDependencies.add(depNode.name);
          }
        }
      }
    }

    const releaseDependencies = this.pkg.releaseDependencies;

    if (Array.isArray(releaseDependencies)) {
      for (const dep of releaseDependencies) {
        if (typeof dep === "string") this.localDependencies.add(dep);
      }
    }

    return this;
  }

  resolveDependencies(packages?: Map<string, Package>) {
    for (const k of Package.depKeys) {
      this[k] = Package.updateDeps(this.pkg[k], packages);
    }
  }

  async resolveCommits(options?: Package.ResolveCommitOptions) {
    options = options || {};
    const basedir = typeof options.basedir === "undefined" ? this.location : path.resolve(options.basedir);
    const relpath = path.relative(basedir, this.location);

    const hash = (this.anchorCommit = await resolveGitRef(this.gitRefs, {
      cwd: basedir,
      cache: options.refCache,
    }));

    this.commits = await getCommits({
      gitRef: hash,
      cwd: basedir,
      cache: options.commitCache,
      filter: !relpath ? undefined : relpath,
    });

    return this;
  }

  resolveVersion() {
    this.version = resolveVersionChange({
      info: this.info,
      type: this.release.type,
      previous: this.anchor ? this.anchor.version : this.release.version,
      prerelease: this.release.prerelease,
    });

    return this;
  }

  /**
   * Check if the dependencies have changed between the anchor and the package.json.
   * @param type The dep type to check for changes (dependencies, peerDependencies, etc)
   */
  hasDepsChanged(type: Package.DepType) {
    let anchorDeps = this.anchor && this.anchor[type];

    // NPM wraps optional dependencies into regular dependencies so we have to specifically remove those from anchor
    // https://github.com/npm/npm/issues/3502
    if (type === "dependencies" && this.pkg.optionalDependencies && anchorDeps) {
      anchorDeps = Object.assign({}, anchorDeps);
      for (const dep of Object.keys(this.pkg.optionalDependencies)) {
        delete anchorDeps[dep];
      }
    }

    return Package.hasDepsChanged(this.pkg[type], anchorDeps, this.localDependencies);
  }

  hasDependencyBreakingRelease(packages: Map<string, Package>) {
    // check direct dependencies for a release
    // all children are checked before descending as a small optimization
    for (const depname of this.localDependencies) {
      const dep = packages.get(depname);
      if (dep && dep.canRelease && dep.release.type === "major") return true;
    }

    // // descend into children tree
    // for (const depname of this.localDependencies) {
    //   const dep = packages.get(depname);
    //   if (dep && dep.hasDependencyBreakingRelease(packages)) return true;
    // }

    return false;
  }

  validate(packages?: Map<string, Package>) {
    this.validations.warnings.clear();
    this.validations.errors.clear();

    // no need to validate if package is private
    if (this.pkg.private) return this;

    if (this.release.type === "skip") {
      if (this.commits.length) {
        this.validations.warnings.add("Package has been modified, but will not be released.");
      }
    } else if (!this.release.hasDeclaredChanges()) {
      this.validations.warnings.add("Package will be released without declared changes.");
    }

    const deps: Package[] = [];
    if (packages) {
      for (const d of this.localDependencies) {
        const dep = packages.get(d);
        if (dep) deps.push(dep);
      }
    }

    if (this.release.type !== "major" && deps.some((d) => d.release.type === "major")) {
      this.validations.warnings.add(
        "At least one dependency has breaking changes. " +
          "It's recommended that this package also receive a major release."
      );
    }

    if (this.access === "public" && deps.some((d) => d.access !== "public")) {
      this.validations.errors.add(
        "Package will be published publicly, but one or more dependency has restricted access."
      );
    }

    if (deps.some((d) => Boolean(d.pkg.private))) {
      this.validations.errors.add("Package has at least one private dependency.");
    }

    if (deps.some((d) => !d.pkg.private && !d.anchor && !d.canRelease)) {
      this.validations.errors.add("Package has at least one dependency that has never been released.");
    }

    if (this.anchorCommit == null) {
      if (this.gitRefs.length) {
        this.validations.warnings.add(
          "Failed to located desired release commits. The initial commit will be used instead."
        );
      } else {
        this.validations.warnings.add("No release commit specified. The initial commit will be used instead.");
      }
    }

    return this;
  }

  toString(options?: { format?: string; color?: boolean; env?: Env }) {
    options = options || {};
    const { color: enableColor, format: _format, env } = options;
    const format = _format ? Package.formats[_format] || _format : Package.formats.default;

    const tpl = createTemplate(format, {
      color: enableColor,
      imports: {
        env,
      },
    });

    return tpl(this);
  }

  async persistChangelog(env: Env, filename = "CHANGELOG.md") {
    if (!this.canRelease) return false;

    // files to stage in git
    const changedFiles: string[] = [];

    // prepend the changelog to the changelog file
    const entry = this.toString({
      format: "changelog_md",
      env,
    });

    let changelog = "";
    const changelogFile = path.join(this.location, filename);

    try {
      changelog = await fs.readFile(changelogFile, "utf-8");
    } catch (err) {
      if (err.code !== "ENOENT") throw err;
    }

    changelog = entry + "\n\n" + changelog;

    await env.safe(() => fs.writeFile(changelogFile, changelog));
    this.logger.debug("prepended changelog to %s", log.color.blue(path.relative(env.basedir, changelogFile)));
    changedFiles.push(changelogFile);

    // reset the RELEASE.yml file
    const releaseFiles = await Release.findFiles(this.location);
    for (const filePath of releaseFiles) {
      if (filePath !== this.release.filePath) {
        changedFiles.push(filePath);
        await env.safe(() => fs.unlink(filePath));
        this.logger.debug("unlink %s", log.color.blue(path.relative(env.basedir, filePath)));
      }
    }

    await this.release.reset().write({
      basedir: env.basedir,
      dryrun: env.dryrun,
      debug: this.logger.debug,
    });

    changedFiles.push(this.release.filePath);

    // add the files to git index
    await env.safeGitExec(
      "add",
      changedFiles.map((f) => path.relative(env.basedir, f))
    );

    return true;
  }

  async npmCommand(env: Env, cmd: string, args?: Record<string, string> | string[], options?: npmUtil.NPMSpawnOptions) {
    const _args = dargs(args);

    await env.safe(() =>
      npmUtil.npmCommand([cmd, ..._args], {
        cwd: this.location,
        ...options,
      })
    );

    const relPath = path.relative(env.basedir, this.location);
    const arglist = _args.map((a) => JSON.stringify(a)).join(" ");
    this.logger.debug(`${relPath ? `[${relPath}] $ ` : ""}npm ${cmd} ${arglist}`);
  }

  async npmPublish(env: Env) {
    let attempts = 0;
    const opts: Record<string, string> = {};

    if (env.npm.distTags) {
      opts.tag = "release-temp";
    }

    if (env.npm.access && this.name[0] === "@") {
      opts.access = this.access;
    }

    while (attempts < 5) {
      try {
        attempts++;

        await this.npmCommand(env, "publish", opts, {
          env: {
            ...process.env,
            NODE_ENV: "production",
          },
        });

        return;
      } catch (e) {
        this.logger.error(e);
        this.logger.warn(`NPM publish for '${this.name}' failed. Retrying...`);

        // wait 5 seconds before trying again
        await new Promise((ok) => setTimeout(ok, 5000));
      }
    }

    throw new Error("Failed to publish to NPM!");
  }

  async publish(env: Env) {
    // only skip private packages
    if (this.pkg.private) return false;

    // always set access, even if skipping the release
    if (!this.version.next) {
      try {
        // only scoped packages can have access set
        if (env.npm.access && this.name[0] === "@") {
          await this.npmCommand(env, "access", [this.access, this.name]);
        }
      } catch (e) {
        this.logger.error(e);
      }

      return false;
    }

    const pkgfile = path.join(this.location, "package.json");
    const pkgsrc = await fs.readFile(pkgfile, "utf8");
    const pkgjson = JSON.parse(pkgsrc) as npmUtil.Package;

    // update version
    pkgjson.version = this.version.next;

    // update dependency versions
    for (const k of Package.depKeys) {
      if (Object.keys(this[k]).length) pkgjson[k] = this[k];
    }

    // set git head (since npm does not)
    pkgjson.gitHead = env.git.head;

    // set the publish config registry if available
    if (env.npm.publishRegistryUrl) {
      pkgjson.publishConfig = {
        registry: env.npm.publishRegistryUrl,
        ...pkgjson.publishConfig,
      };
    }

    if (env.git.remote && typeof pkgjson.repository !== "string") {
      pkgjson.repository = {
        type: "git",
        url: `ssh://git@${env.git.remote.resource}/${env.git.remote.full_name}.git`,
        directory: path.relative(env.basedir, this.location),
        ...pkgjson.repository,
      };
    }

    // write package.json
    this.logger.verbose("publish package.json", pkgjson);
    await env.safe(() => fs.writeFile(pkgfile, JSON.stringify(pkgjson, null, 2)));

    // publish to NPM
    await this.npmPublish(env);

    return true;
  }

  async switchReleaseDistTag(env: Env) {
    if (!env.npm.distTags || !this.version.next) return;

    try {
      await this.npmCommand(env, "dist-tag", ["add", `${this.name}@${this.version.next}`, this.distTag]);
      await this.npmCommand(env, "dist-tag", ["rm", this.name, "release-temp"]);
    } catch (err) {
      this.logger.warn(`Error while switching temporary dist-tag on package '${this.name}'.`);
    }
  }
}
